import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { createLocalStorageTokenStorage } from "@ecommerce/auth-core";
import type { AuthMode, AuthSessionEvent, SessionListener } from "@ecommerce/auth-core";

export interface AxiosHttpClientConfig {
    tenantName: string;
    authMode?: AuthMode;
    apiProtocol?: string;
    apiDomain?: string;
    apiVersion?: string;
    apiTimeout?: number;
    onLogout?: () => void;
    onSessionChanged?: (event: AuthSessionEvent) => void | Promise<void>;
    logoutRedirectUrl?: string | (() => string);
    userStorage?: { clear: () => void };
}

type RequestOptions = AxiosRequestConfig & {
    body?: unknown;
    method?: string;
    skipAuth?: boolean;
    authTokenOverride?: string;
};

type SessionEventType = Extract<AuthSessionEvent["type"], "LOGIN" | "REGISTER">;

export interface AxiosHttpClient {
    authMode: AuthMode;
    get: <T>(url: string, config?: RequestOptions) => Promise<T>;
    post: <T>(url: string, data?: unknown, config?: RequestOptions) => Promise<T>;
    put: <T>(url: string, data?: unknown, config?: RequestOptions) => Promise<T>;
    patch: <T>(url: string, data?: unknown, config?: RequestOptions) => Promise<T>;
    delete: <T>(url: string, config?: RequestOptions) => Promise<T>;
    getWithQueryParamsSerializer: <T>(
        url: string,
        params: unknown,
        config?: RequestOptions,
    ) => Promise<T>;
    request: <T>(path: string, options?: RequestOptions) => Promise<T>;
    postSession: <T>(path: string, data?: unknown) => Promise<T>;
    acceptSession: (accessToken: string, eventType: SessionEventType) => Promise<void>;
    notifySessionChanged: (event: AuthSessionEvent) => Promise<void>;
    subscribeSessionChanged: (listener: SessionListener) => () => void;
    setAccessToken: (accessToken: string | null) => void;
    getAccessToken: () => string | null;
    refreshSession: () => Promise<string>;
    logoutSession: () => Promise<void>;
    logout: () => void;
    updateAuthHeader: () => void;
    axiosInstance: AxiosInstance;
    axiosInstanceRefresh: AxiosInstance;
    tokenStorage: ReturnType<typeof createLocalStorageTokenStorage>;
    urlAPI: string;
}

const COOKIE_MODE: AuthMode = "http-only-cookie";
const LEGACY_MODE: AuthMode = "legacy-local-refresh";
const STORE_CLIENT_HEADER = "X-Codi-Tienda-Client";

export const createAxiosHttpClient = (config: AxiosHttpClientConfig): AxiosHttpClient => {
    const authMode = config.authMode ?? LEGACY_MODE;
    const tokenStorageKey = `${config.tenantName}-tokens`;
    const apiProtocol =
        config.apiProtocol ||
        (typeof window !== "undefined" && (window as any).API_PROTOCOL) ||
        "https";
    const apiDomain =
        config.apiDomain ||
        (typeof window !== "undefined" && (window as any).API_DOMAIN) ||
        "localhost:44381/api";
    const apiVersion =
        config.apiVersion || (typeof window !== "undefined" && (window as any).API_VERSION) || "v1";
    const apiTimeout =
        config.apiTimeout ||
        (typeof window !== "undefined" && (window as any).API_TIMEOUT) ||
        60000;
    const urlAPI = `${apiProtocol}://${apiDomain}/${apiVersion}/`;

    const tokenStorage = createLocalStorageTokenStorage(tokenStorageKey);

    /*
    Cookie mode tolera el registro legacy durante la migracion, pero lo
    reescribe inmediatamente sin refresh token.
  */
    if (authMode === COOKIE_MODE) {
        const legacyAccess = tokenStorage.getAccessToken();
        if (legacyAccess) {
            tokenStorage.saveAccessToken(legacyAccess);
        }
    }

    const listeners = new Set<SessionListener>();
    let refreshPromise: Promise<string> | null = null;
    let logoutInProgress = false;

    const axiosInstance = axios.create({
        baseURL: urlAPI,
        timeout: apiTimeout,
        headers: {
            "Content-Type": "application/json",
        },
    });

    /*
    Instancia aislada para las cuatro rutas session. Nunca hereda
    Authorization y sólo ella usa cookies cross-origin.
  */
    const sessionAxios = axios.create({
        baseURL: urlAPI,
        timeout: apiTimeout,
        withCredentials: authMode === COOKIE_MODE,
        headers: {
            "Content-Type": "application/json",
            ...(authMode === COOKIE_MODE ? { [STORE_CLIENT_HEADER]: "1" } : {}),
        },
    });

    const getAccessToken = () =>
        authMode === COOKIE_MODE
            ? tokenStorage.getAccessToken()
            : (tokenStorage.get()?.accessToken ?? null);

    const updateAuthHeader = () => {
        const accessToken = getAccessToken();
        if (accessToken) {
            axiosInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        } else {
            delete axiosInstance.defaults.headers.common.Authorization;
        }
    };

    const setAccessToken = (accessToken: string | null) => {
        if (!accessToken) {
            tokenStorage.clear();
            updateAuthHeader();
            return;
        }

        if (authMode === COOKIE_MODE) {
            tokenStorage.saveAccessToken(accessToken);
        } else {
            const current = tokenStorage.get();
            if (current?.refreshToken) {
                tokenStorage.save({
                    accessToken,
                    refreshToken: current.refreshToken,
                });
            }
        }

        logoutInProgress = false;
        updateAuthHeader();
    };

    updateAuthHeader();

    const notifySessionChanged = async (event: AuthSessionEvent) => {
        await config.onSessionChanged?.(event);
        await Promise.all(
            Array.from(listeners).map((listener) => Promise.resolve(listener(event))),
        );
    };

    const subscribeSessionChanged = (listener: SessionListener) => {
        listeners.add(listener);
        return () => listeners.delete(listener);
    };

    const getLogoutRedirectUrl = () => {
        try {
            const configuredUrl =
                typeof config.logoutRedirectUrl === "function"
                    ? config.logoutRedirectUrl()
                    : config.logoutRedirectUrl;

            if (configuredUrl) return configuredUrl;
        } catch {
            // Una estrategia de retorno propia no debe impedir cerrar la sesion.
        }

        return `${window.location.origin}/login`;
    };

    const clearLocalSession = (redirect: boolean) => {
        if (logoutInProgress) return;
        logoutInProgress = true;

        tokenStorage.clear();
        config.userStorage?.clear();
        updateAuthHeader();
        config.onLogout?.();
        void notifySessionChanged({ type: "LOGOUT" });

        if (redirect && typeof window !== "undefined") {
            window.location.href = getLogoutRedirectUrl();
        }
    };

    const normalizeAccessToken = (payload: unknown): string | null => {
        if (typeof payload === "string") {
            return payload.trim() || null;
        }

        if (
            payload &&
            typeof payload === "object" &&
            "accessToken" in payload &&
            typeof payload.accessToken === "string"
        ) {
            return payload.accessToken.trim() || null;
        }

        return null;
    };

    const performRefresh = async (): Promise<string> => {
        let response: AxiosResponse<unknown>;

        if (authMode === COOKIE_MODE) {
            response = await sessionAxios.post("auth/session/refresh-tokens", {});
        } else {
            const refreshToken = tokenStorage.get()?.refreshToken;
            if (!refreshToken) {
                throw new Error("No hay refresh token disponible.");
            }

            response = await sessionAxios.post(
                "auth/refresh-tokens",
                {},
                {
                    headers: {
                        Authorization: `Bearer ${refreshToken}`,
                    },
                },
            );
        }

        const accessToken = normalizeAccessToken(response.data);
        if (!accessToken) {
            throw new Error("La renovacion no devolvio un access token.");
        }

        setAccessToken(accessToken);
        await notifySessionChanged({
            type: "REFRESH",
            accessToken,
        });
        return accessToken;
    };

    const refreshSession = (): Promise<string> => {
        if (refreshPromise) return refreshPromise;

        refreshPromise = performRefresh()
            .catch((error) => {
                clearLocalSession(true);
                throw error;
            })
            .finally(() => {
                refreshPromise = null;
            });

        return refreshPromise;
    };

    const prepareConfig = (requestConfig?: RequestOptions): AxiosRequestConfig => {
        if (!requestConfig) return {};

        const {
            skipAuth,
            authTokenOverride,
            body: _body,
            method: _method,
            ...axiosConfig
        } = requestConfig;

        if (skipAuth || authTokenOverride) {
            axiosConfig.headers = {
                ...axiosConfig.headers,
                Authorization: authTokenOverride ? `Bearer ${authTokenOverride}` : undefined,
            };
        }

        return axiosConfig;
    };

    const toApplicationError = (error: AxiosError): Error => {
        let message = "Ocurrio un error inesperado.";
        const data = error.response?.data;

        if (typeof data === "string") {
            message = data;
        } else if (data && typeof data === "object") {
            if ("descripcionError" in data && typeof data.descripcionError === "string") {
                message = data.descripcionError;
            } else if ("message" in data && typeof data.message === "string") {
                message = data.message;
            } else if (error.response) {
                message = `Error ${error.response.status}.`;
            }
        } else if (error.request) {
            message = "No se pudo conectar con el servidor.";
        }

        const applicationError = new Error(message) as Error & {
            cause?: unknown;
        };
        applicationError.cause = error;
        return applicationError;
    };

    const execute = async <T>(
        operation: () => Promise<AxiosResponse<T>>,
        retry: () => Promise<AxiosResponse<T>>,
        allowRefresh = true,
    ): Promise<T> => {
        try {
            const response = await operation();
            if (typeof window !== "undefined" && (window as any).DEBUG_HTTP) {
                console.log(`HTTP ${response.status} ${response.config.url}`);
            }
            return response.data;
        } catch (unknownError) {
            const error = unknownError as AxiosError;
            if (allowRefresh && error.response?.status === 401 && getAccessToken()) {
                await refreshSession();
                return execute(retry, retry, false);
            }

            throw toApplicationError(error);
        }
    };

    const get = <T>(url: string, requestConfig?: RequestOptions) => {
        const run = () => axiosInstance.get<T>(url, prepareConfig(requestConfig));
        return execute(run, run);
    };

    const post = <T>(url: string, data?: unknown, requestConfig?: RequestOptions) => {
        const run = () => axiosInstance.post<T>(url, data, prepareConfig(requestConfig));
        return execute(run, run);
    };

    const put = <T>(url: string, data?: unknown, requestConfig?: RequestOptions) => {
        const run = () => axiosInstance.put<T>(url, data, prepareConfig(requestConfig));
        return execute(run, run);
    };

    const patch = <T>(url: string, data?: unknown, requestConfig?: RequestOptions) => {
        const run = () => axiosInstance.patch<T>(url, data, prepareConfig(requestConfig));
        return execute(run, run);
    };

    const del = <T>(url: string, requestConfig?: RequestOptions) => {
        const run = () => axiosInstance.delete<T>(url, prepareConfig(requestConfig));
        return execute(run, run);
    };

    const getWithQueryParamsSerializer = <T>(
        url: string,
        params: unknown,
        requestConfig?: RequestOptions,
    ) =>
        get<T>(url, {
            ...requestConfig,
            params,
            paramsSerializer: { indexes: null },
        });

    const postSession = async <T>(path: string, data?: unknown): Promise<T> => {
        try {
            const response = await sessionAxios.post<T>(path, data);
            return response.data;
        } catch (unknownError) {
            throw toApplicationError(unknownError as AxiosError);
        }
    };

    const acceptSession = async (accessToken: string, eventType: SessionEventType) => {
        setAccessToken(accessToken);
        await notifySessionChanged({
            type: eventType,
            accessToken,
        });
    };

    const logoutSession = async () => {
        try {
            if (authMode === COOKIE_MODE) {
                await sessionAxios.post("auth/session/logout", {});
            }
        } finally {
            clearLocalSession(false);
        }
    };

    const request = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
        const method = (options.method ?? "GET").toUpperCase();
        const body = options.body;

        switch (method) {
            case "POST":
                return post<T>(path, body, options);
            case "PUT":
                return put<T>(path, body, options);
            case "PATCH":
                return patch<T>(path, body, options);
            case "DELETE":
                return del<T>(path, options);
            default:
                return get<T>(path, options);
        }
    };

    return {
        authMode,
        get,
        post,
        put,
        patch,
        delete: del,
        getWithQueryParamsSerializer,
        request,
        postSession,
        acceptSession,
        notifySessionChanged,
        subscribeSessionChanged,
        setAccessToken,
        getAccessToken,
        refreshSession,
        logoutSession,
        logout: () => clearLocalSession(true),
        updateAuthHeader,
        axiosInstance,
        axiosInstanceRefresh: sessionAxios,
        tokenStorage,
        urlAPI,
    };
};
